import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  AutocompleteRenderInputParams,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  Stack,
  Chip,
  Divider,
  Paper,
  Modal,
} from '@mui/material';
import {
  countryListWithCode,
  MARKET_FINDER_COLUMN_METADATA,
  MARKET_FINDER_COLUMN_METADATA_ORDER,
  MONTHLY_BEST_MARKET_FINDER_LIMIT,
  SAVE_USER_DATA_LAMBDA_URL,
  SAVED_MARKETS_POPUP_MESSAGE_THRESHOLD,
  usStateListWithCode,
  macro_to_subgroups,
  macro_group_legends,
  sub_group_legends,
} from 'src/const';
import { callApi } from 'src/core/callApi';
import { User } from 'src/interfaces/iuser';
import _ from 'lodash';
import { areArraysEqual, convertToUSD } from 'src/utils/functions';
import FilterBar from './FilterBar';
import SelectedFiltersDisplay from './SelectedFiltersDisplay';
import MarketDataGrid, { MarketDataRow } from './MarketDataGrid';
import { DUMMY_MARKET_FINDER_DATA } from './dymmy_markets_data';
import { useCanAccessExpertPlan } from 'src/hooks/useFeatureAccess';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingCard } from 'src/App/HOC';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { getUserInfo } from 'src/store/Dashboard/actions';
import AutohideSnackbar from 'src/App/Components/Alerts/AutohideSnackbar';

// Define blur states
type BlurState = 'none' | 'blurMarket' | 'blurAll' | 'blurPartial';

// Define type for market data with blur state by intersecting with MarketDataRow
type MarketDataWithBlur = MarketDataRow & {
  _blurState: BlurState;
};

// ===============================
// Filter Options (Moved from FilterBar)
// ===============================
const regulationOptions = [
  { value: 'strict', label: 'Strict' },
  { value: 'lenient', label: 'Lenient' },
];
const marketSizeOptions = [
  { value: 'big', label: 'Large' },
  { value: 'medium', label: 'Medium' },
  { value: 'small', label: 'Small' },
];
const sortByOptions = [
  { value: 'supply', label: 'Number of Listings' },
  { value: 'short_term_rental_yield', label: 'Short-Term Rental Yield' },
  { value: 'rent_gap', label: 'Rent Gap' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'yoy_supply_change', label: 'YoY Supply Change' },
  { value: 'yoy_revenue_change', label: 'YoY Revenue Change' },
];

const formatSortByOptions = (sortByOptions: any[], budgetType: 'buy' | 'rent') => {
  return sortByOptions.filter(option => {
    if (budgetType === 'buy') {
      return option.value !== 'rent_gap';
    }
    if (budgetType === 'rent') {
      return option.value !== 'short_term_rental_yield';
    }
    return true;
  });
};

// Transform country list and add US states
const countryOptions = countryListWithCode.flatMap(c => {
  const countryOption = {
    value: c.code,
    label: c.label.trim(),
    isExpandable: c.code === 'US',
    parentValue: null as string | null,
    isChild: false,
  };
  if (c.code === 'US') {
    const stateOptions = usStateListWithCode.map(s => ({
      value: `US-${s.code}`,
      label: s.label.trim(),
      isChild: true,
      parentValue: 'US',
      isExpandable: false,
    }));
    return [countryOption, ...stateOptions];
  }
  return [countryOption];
});

// Helper to find label for a given value in options array
const findLabel = (
  options: { value: string; label: string }[], // Simplified, as specific child/parent logic isn't needed here anymore
  value: string
): string => {
  const option = options.find(opt => opt.value === value);
  return option ? option.label : '';
};

const CreditWarningModal = ({
  open,
  handleClose,
  handleOnClick,
}: {
  open: boolean;
  handleClose: () => void;
  handleOnClick: () => Promise<void>;
}) => {
  const user: User = useSelector(state => (state as any).dashboard.user);
  const bestMarketFinderUsage = user.bestMarketFinderUsage;

  // YYYY-MM
  const currentYearMonthString = new Date().toISOString().slice(0, 7);
  const creditsUsed = bestMarketFinderUsage[currentYearMonthString] || 0;
  const totalCredits = MONTHLY_BEST_MARKET_FINDER_LIMIT[user.subscriptionPlan];
  const creditsRemaining = Math.max(0, totalCredits - creditsUsed);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(max(300px, 90vw), 500px)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: '30px',
          borderRadius: '12px',
          outline: 'none',
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: '10px',
            right: '10px',
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
        <Typography variant="h6" sx={{ mb: '20px' }}>
          Searching for markets will use one credit. Proceed to confirm your search. You have{' '}
          {creditsRemaining} credits remaining for this month.
        </Typography>
        <Button
          variant="contained"
          disabled={creditsRemaining < 1}
          onClick={() => {
            handleOnClick();
            handleClose();
          }}
          sx={{ float: 'right' }}
        >
          Proceed
        </Button>
      </Box>
    </Modal>
  );
};

// ===============================
// Main component
// ===============================

// Define labels for each filter type used in grouped chips
// const filterTypeLabels: { [key: string]: string } = {
//   location: 'Location',
//   regulation: 'Regulation',
//   marketSize: 'Market Size',
// };

// Helper to get parent macro for a subgroup
const getParentMacro = (
  subgroupValue: string,
  allMacroToSubgroups: typeof macro_to_subgroups
): string | null => {
  for (const [macro, subgroups] of Object.entries(allMacroToSubgroups)) {
    if (subgroups.includes(subgroupValue)) {
      return macro;
    }
  }
  return null;
};

// Build regionOptions for the dropdown (macro and subgroups), flat array for grouping
const regionOptions = Object.entries(macro_to_subgroups).flatMap(([macro, subgroups]) => {
  const macroOption = {
    value: macro,
    label: macro_group_legends[macro],
    isExpandable: true, // Retained for potential use by Autocomplete grouping
    parentValue: null as string | null,
    isChild: false,
  };
  const subOptions = subgroups.map(sub => ({
    value: sub, // CHANGED: Was `${macro}-${sub}`
    label: sub_group_legends[sub],
    isChild: true,
    parentValue: macro, // IMPORTANT: Stores the parent macro code
    isExpandable: false,
  }));
  return [macroOption, ...subOptions];
});

const MarketExplorerPage = ({
  currency,
  handleOnSampleClick,
  isMobileScreen,
  OnBoardingSearchBox,
  setMarketComparisons,
  isLoggedIn,
  handleSidebarClick,
  handleSidebarListItemClick
}: {
  currency: string;
  handleOnSampleClick: any;
  isMobileScreen: boolean;
  OnBoardingSearchBox: any;
  setMarketComparisons: any;
  isLoggedIn: boolean;
  handleSidebarClick?: any;
  handleSidebarListItemClick?: any;
}) => {
  const urlParams = useRef<URLSearchParams>(new URLSearchParams(window.location.search));
  const urlCountryCode = urlParams.current.get('countryCode');

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [markets, setMarkets] = useState<any[]>([]);
  const [marketSearchInfo, setMarketSearchInfo] = useState<{
    searchName: string;
    countryCode: string;
  } | null>(null);
  const userCountryCode = useSelector(state => (state as any).dashboard.userCountryCode);
  const savedMarketsState = useSelector((state: any) => state.dashboard.savedMarkets);
  const {
    data: selectedList,
    loading: selectedListLoading,
    lastSavedMarketNames,
    openSavedMarketsCTAModal,
    isError: selectedListError,
  } = savedMarketsState;

  const [showSavedMarketLoadingError, setShowSavedMarketLoadingError] = useState(false);

  const defaultMarketConfig = useMemo(
    () => ({
      region: [],
      location: [urlCountryCode ? urlCountryCode.toUpperCase() : userCountryCode],
      regulation: ['lenient'],
      marketSize: [],
      budget: {
        type: 'buy' as 'buy' | 'rent',
        range: { min: 0, max: 0 },
      },
      sortBy: 'supply',
    }),
    [userCountryCode, urlCountryCode]
  );

  const [currencSearchedMarket, setCurrencSearchedMarket] = useState<{
    region: string[];
    location: string[];
    regulation: string[];
    marketSize: string[];
    budget: {
      type: 'buy' | 'rent';
      range: { min: number; max: number };
    };
    sortBy: string;
  }>({
    region: defaultMarketConfig.region,
    location: defaultMarketConfig.location,
    regulation: defaultMarketConfig.regulation,
    marketSize: defaultMarketConfig.marketSize,
    budget: defaultMarketConfig.budget,
    sortBy: defaultMarketConfig.sortBy,
  });

  // State for filters - selectedLocations now holds country codes and state codes
  const [selectedLocations, setSelectedLocations] = useState<string[]>(
    defaultMarketConfig.location
  );
  const [selectedRegulations, setSelectedRegulations] = useState<string[]>(
    defaultMarketConfig.regulation
  );
  const [selectedMarketSizes, setSelectedMarketSizes] = useState<string[]>(
    defaultMarketConfig.marketSize
  );
  const [selectedSortBy, setSelectedSortBy] = useState<string>(defaultMarketConfig.sortBy);
  const [selectedBudget, setSelectedBudget] = useState<{
    type: 'buy' | 'rent';
    range: { min: number; max: number };
  }>(defaultMarketConfig.budget);

  const [creditDeductModalOpen, setCreditDeductModalOpen] = useState<boolean>(false);

  const [currentRequestType, setCurrentRequestType] = useState<'free' | 'premium'>('free');

  const isFreeUser = !useCanAccessExpertPlan();

  const dispatch = useDispatch();

  const [filtersChanged, setFiltersChanged] = useState(false);

  const [selectedRegions, setSelectedRegions] = useState<string[]>(defaultMarketConfig.region);

  const fetchMarkets = useCallback(
    async (deductCredit: boolean = false, isFilterOnlyChange: boolean = false) => {
      try {
        setFiltersChanged(false);
        const requestBody: any = {
          lock_type: 'v2',
        };

        setIsLoading(true);
        setMarkets([]);

        // Handle Region Body
        if (selectedRegions.length > 0) {
          // selectedRegions now contains direct macro or sub-group codes
          requestBody.loc_group_codes = selectedRegions;
        }

        // Handle Location Body
        if (selectedLocations.length > 0) {
          const countryCodeArray: Set<string> = new Set();
          const usStatesArray: Set<string> = new Set();
          for (const location of selectedLocations) {
            if (location.startsWith('US-')) {
              usStatesArray.add(location.split('-')[1]);
              countryCodeArray.add('US');
            } else {
              countryCodeArray.add(location);
            }
          }
          requestBody.country_codes = Array.from(countryCodeArray);
          requestBody.us_state_codes = Array.from(usStatesArray);
        }

        // Handle Regulation Body
        if (selectedRegulations.length > 0) {
          requestBody.regulations = selectedRegulations;
        }

        // Handle Market Size Body
        if (selectedMarketSizes.length > 0) {
          requestBody.market_sizes = selectedMarketSizes;
        }

        // Handle Sort By Body
        if (selectedSortBy) {
          requestBody.sort_by = selectedSortBy;
        }

        const isPremiumRequest = deductCredit || isFilterOnlyChange;
        requestBody.is_premium_request = isPremiumRequest;
        setCurrentRequestType(isPremiumRequest ? 'premium' : 'free');

        if (!isPremiumRequest && selectedSortBy !== 'supply') {
          setSelectedSortBy('supply');
          requestBody.sort_by = 'supply';
        }

        if (selectedBudget.range.min > 0 || selectedBudget.range.max > 0) {
          const minValue =
            currency === 'USD'
              ? selectedBudget.range.min
              : Math.round(convertToUSD(currency, selectedBudget.range.min));
          const maxValue =
            currency === 'USD'
              ? selectedBudget.range.max
              : Math.round(convertToUSD(currency, selectedBudget.range.max));

          requestBody.budget = {
            type: selectedBudget.type,
            range: [minValue, maxValue],
          };
        }

        const URL = 'https://niyu2lp99j.execute-api.us-east-2.amazonaws.com/default/getBestMarkets';
        const response = await callApi(URL, { body: requestBody }, true, false, '', 'POST', true);

        if (response && response.message && response.message.markets) {
          setMarkets(response.message.markets);
          setCurrencSearchedMarket({
            region: selectedRegions,
            location: selectedLocations,
            regulation: selectedRegulations,
            marketSize: selectedMarketSizes,
            budget: selectedBudget,
            sortBy: selectedSortBy,
          });
          if (deductCredit && response.message?.markets?.length > 0) {
            callApi(
              SAVE_USER_DATA_LAMBDA_URL,
              { queryParams: { best_market_finder_usage: true } },
              true,
              false,
              '',
              'GET',
              true
            ).then(() => {
              dispatch(getUserInfo());
            });
          }
        }
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching markets:', error);
        setIsLoading(false);
      }
    },
    [
      selectedRegions,
      selectedLocations,
      selectedRegulations,
      selectedMarketSizes,
      selectedSortBy,
      selectedBudget,
      currency,
      dispatch,
      setCurrencSearchedMarket,
      setCurrentRequestType,
      setFiltersChanged,
      setIsLoading,
      setMarkets,
      // Assuming callApi, convertToUSD, SAVE_USER_DATA_LAMBDA_URL, getUserInfo are stable
    ]
  );

  useEffect(() => {
    if (urlCountryCode) {
      urlParams.current.delete('countryCode');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [urlCountryCode]);

  useEffect(() => {
    const isFiltersChanged =
      JSON.stringify({
        region: currencSearchedMarket.region,
        location: currencSearchedMarket.location,
        regulation: currencSearchedMarket.regulation,
        marketSize: currencSearchedMarket.marketSize,
        budget: currencSearchedMarket.budget,
      }) ===
      JSON.stringify({
        region: selectedRegions,
        location: selectedLocations,
        regulation: selectedRegulations,
        marketSize: selectedMarketSizes,
        budget: selectedBudget,
      });

    if (
      currentRequestType === 'premium' &&
      isFiltersChanged 
      && selectedSortBy !== currencSearchedMarket.sortBy
    ) {
      fetchMarkets(false, true);
    } else if (!isFiltersChanged) {
      fetchMarkets(false);
    } else if (selectedSortBy !== currencSearchedMarket.sortBy) {
      setFiltersChanged(true);
    }
  }, [
    selectedSortBy,
    selectedRegions,
    selectedLocations,
    selectedRegulations,
    selectedMarketSizes,
    selectedBudget,
    currencSearchedMarket,
    currentRequestType,
    isFreeUser,
  ]);

  useEffect(() => {
    if (marketSearchInfo && marketSearchInfo.countryCode) {
      setSelectedLocations([marketSearchInfo.countryCode]);
    }
  }, [marketSearchInfo]);

  useEffect(() => {
    setShowSavedMarketLoadingError(selectedListError);
  }, [selectedListError]);

  const isFilterConfigIsDefault = useCallback(() => {
    return (
      areArraysEqual(selectedRegions, defaultMarketConfig.region) &&
      areArraysEqual(selectedLocations, defaultMarketConfig.location) &&
      areArraysEqual(selectedRegulations, defaultMarketConfig.regulation) &&
      areArraysEqual(selectedMarketSizes, defaultMarketConfig.marketSize) &&
      selectedSortBy === defaultMarketConfig.sortBy
    );
  }, [
    selectedRegions,
    selectedLocations,
    selectedRegulations,
    selectedMarketSizes,
    selectedSortBy,
    defaultMarketConfig,
  ]);

  useEffect(() => {
    fetchMarkets(false);
  }, []);

  useEffect(() => {
    if (selectedBudget.type === 'rent' && selectedSortBy === 'short_term_rental_yield') {
      setSelectedSortBy('rent_gap');
    }
    if (selectedBudget.type === 'buy' && selectedSortBy === 'rent_gap') {
      setSelectedSortBy('short_term_rental_yield');
    }
  }, [selectedBudget]);

  const onFilterChangeAccessCheckMiddleware = (val: string | string[], actionFunction) => {
    if (isFreeUser) {
      handleOnSampleClick('expert');
    } else {
      actionFunction(val);
    }
  };

  // Handlers for FilterDropdown (Moved from FilterBar)
  // Updated handler for country/state selection
  const handleLocationsChange = (newSelection: string[]) => {
    let updatedSelection = [...newSelection];

    const includesUS = newSelection.includes('US');
    const includesStates = newSelection.some(val => val.startsWith('US-'));

    if (includesUS && includesStates) {
      // If US is selected AND specific states are selected, prioritize states
      // Remove 'US' from the selection
      updatedSelection = updatedSelection.filter(val => val !== 'US');
    } else if (
      includesUS &&
      !newSelection.some(val => val.startsWith('US-') && updatedSelection.includes(val))
    ) {
      // If 'US' was just added and no states were previously selected, clear any states that might have been added implicitly by the component before this handler ran
      // (This logic might depend on how the Autocomplete component behaves with hierarchical data)
      // Keep only 'US' if it was selected directly. Assume selecting 'US' implies all states.
      updatedSelection = updatedSelection.filter(val => !val.startsWith('US-') || val === 'US');
    } else if (!includesUS && includesStates) {
      // If US is not selected but states are, ensure 'US' is not present
      updatedSelection = updatedSelection.filter(val => val !== 'US');
    } else if (
      newSelection.some(val => val.startsWith('US-')) &&
      !includesUS &&
      !selectedLocations.includes('US')
    ) {
      // If a state was just added, and 'US' wasn't previously selected, make sure 'US' isn't added.
      updatedSelection = updatedSelection.filter(val => val !== 'US');
    }

    // Handle the case where the user explicitly adds 'US' *after* states have been selected.
    // The requirement: "If the user manually selects any individual state(s), automatically unselect the main 'United States' option."
    // And: "Conversely, if 'United States' is selected, clear any individual state selections"
    const justAddedUS = newSelection.includes('US') && !selectedLocations.includes('US');
    const hasStatesSelected = selectedLocations.some(loc => loc.startsWith('US-'));

    if (justAddedUS) {
      // If US was just added, remove all states
      updatedSelection = newSelection.filter(loc => loc === 'US' || !loc.startsWith('US-'));
    } else if (
      newSelection.some(loc => loc.startsWith('US-')) &&
      !selectedLocations.some(loc => loc.startsWith('US-'))
    ) {
      // If a state was just added (and no states were selected before), ensure 'US' is removed
      updatedSelection = newSelection.filter(loc => loc !== 'US');
    }

    setSelectedLocations(updatedSelection);
  };
  const handleRegulationsChange = (val: string[] | string) => {
    if (Array.isArray(val)) setSelectedRegulations(val);
  };
  const handleMarketSizesChange = (val: string[] | string) => {
    if (Array.isArray(val)) setSelectedMarketSizes(val);
  };
  const handleSortByChange = (val: string[] | string) => {
    if (isFreeUser) {
      handleOnSampleClick('expert');
    } else {
      if (typeof val === 'string') setSelectedSortBy(val);
    }
  };

  const handleBudgetChange = (values: { min: number; max: number }) => {
    setSelectedBudget(prev => ({ ...prev, range: values }));
  };

  const handleBudgetModeChange = (newMode: 'buy' | 'rent') => {
    setSelectedBudget(prev => ({
      type: newMode,
      range: { min: 0, max: 0 }, // Reset range when changing mode
    }));
  };

  // Individual filter removal handlers
  const handleRemoveLocation = (valueToRemove: string) => {
    setSelectedLocations(prev => prev.filter(loc => loc !== valueToRemove));
  };

  const handleRemoveRegulation = (valueToRemove: string) => {
    setSelectedRegulations(prev => prev.filter(reg => reg !== valueToRemove));
  };

  const handleRemoveMarketSize = (valueToRemove: string) => {
    setSelectedMarketSizes(prev => prev.filter(size => size !== valueToRemove));
  };

  const handleRegionsChange = (currentFullSelection: string[]) => {
    const finalSelection = new Set<string>();

    const newSelectedMacros = new Set<string>();
    const newSelectedSubregions = new Set<string>();

    currentFullSelection.forEach(item => {
      if (macro_to_subgroups[item]) {
        // Item is a macro
        newSelectedMacros.add(item);
      } else {
        // Item is potentially a subregion
        // Check if it's a known subregion (i.e., it has a parent macro)
        const parent = getParentMacro(item, macro_to_subgroups);
        if (parent) {
          newSelectedSubregions.add(item);
        }
        // If item is not a macro and not a known subregion, it's ignored.
        // This assumes currentFullSelection contains valid values from regionOptions.
      }
    });

    // Add macros to final selection
    newSelectedMacros.forEach(macro => finalSelection.add(macro));

    // Add subregions to final selection, but only if their parent macro is NOT selected
    newSelectedSubregions.forEach(subregion => {
      const parent = getParentMacro(subregion, macro_to_subgroups);
      // Add subregion if it has no parent (should not happen for valid subregions)
      // OR if its parent macro is not in the set of selected macros.
      if (!parent || !newSelectedMacros.has(parent)) {
        finalSelection.add(subregion);
      }
    });

    setSelectedRegions(Array.from(finalSelection));
  };

  const handleRemoveRegion = (valueToRemove: string) => {
    setSelectedRegions(prev => prev.filter(region => region !== valueToRemove));
  };

  // Process markets data to add blur state for free users
  const processedMarkets = useMemo((): MarketDataWithBlur[] => {
    let effectiveMarkets = [...markets]; // Start with a copy of the API markets

    if (marketSearchInfo) {
      const lockedMarketRow: Omit<MarketDataRow, 'rank'> & { rank: number | string } = {
        rank: 'locked', // Special value for the lock icon
        name: marketSearchInfo.searchName,
        // --- Placeholder/Dummy values for other MarketDataRow properties ---
        // Ensure these match MarketDataRow structure from MarketDataGrid.tsx
        short_term_rental_yield: 'locked',
        rent_gap: 'locked',
        occupancy_rate: 'locked',
        adr: 'locked',
        revenue: 'locked',
        supply: 'locked',
        regulation: 'locked',
        yoy_supply_change: 'locked',
        yoy_revenue_change: 'locked',
      };
      // Prepend the locked row
      effectiveMarkets.unshift(lockedMarketRow as MarketDataRow);
    }

    // Return markets with blur state based on 'locked' values
    return effectiveMarkets.map(market => {
      // Check each field in the market data
      const hasLockedValues = Object.values(market).some(value => value === 'locked');
      return {
        ...market,
        _blurState: hasLockedValues ? 'blurAll' : 'none',
      } as MarketDataWithBlur;
    });
  }, [markets, marketSearchInfo]);

  return (
    <>
      <Grid container sx={{ mt: '30px', mb: '30px', ...(isMobileScreen ? { px: '16px' } : {}) }}>
        <Grid item xs={12} sx={{ mb: '10px' }}>
          <Typography variant="h2" sx={{ fontSize: '24px', fontWeight: 'bold' }}>
            Market Explorer
          </Typography>
          <Typography variant="body1" sx={{ mt: '10px' }}>
            Explore the most profitable short-term rental markets that fit your strategy.
          </Typography>
        </Grid>

        {filtersChanged && (
          <AutohideSnackbar
            message="Please unlock the search to enable filter changes."
            type="warning"
            autoHideDuration={5000}
            handleOnCloseSideEffect={() => {
              setFiltersChanged(false);
            }}
          />
        )}

        <Grid item xs={12}>
          <FilterBar
            selectedRegions={selectedRegions}
            regionOptions={regionOptions}
            handleRegionsChange={handleRegionsChange}
            selectedLocations={selectedLocations}
            selectedRegulations={selectedRegulations}
            selectedMarketSizes={selectedMarketSizes}
            selectedBudget={{
              buy: selectedBudget.type === 'buy' ? selectedBudget.range : undefined,
              rent: selectedBudget.type === 'rent' ? selectedBudget.range : undefined,
            }}
            selectedBudgetMode={selectedBudget.type}
            handleLocationsChange={handleLocationsChange}
            handleBudgetChange={(type: string, values: { min: number; max: number }) => {
              setSelectedBudget({
                type: type as 'buy' | 'rent',
                range: values,
              });
            }}
            handleBudgetModeChange={handleBudgetModeChange}
            handleRegulationsChange={handleRegulationsChange}
            handleMarketSizesChange={handleMarketSizesChange}
            countryOptions={countryOptions}
            regulationOptions={regulationOptions}
            marketSizeOptions={marketSizeOptions}
            onFilterChangeAccessCheckMiddleware={onFilterChangeAccessCheckMiddleware}
            openSearchModal={() =>
              !isFreeUser ? setCreditDeductModalOpen(true) : handleOnSampleClick('expert')
            }
            isMobileScreen={isMobileScreen}
            OnBoardingSearchBox={OnBoardingSearchBox}
            setMarketSearchInfo={setMarketSearchInfo}
            setMarketComparisons={setMarketComparisons}
            isFreeUser={isFreeUser}
            currency={currency}
            handleSidebarClick={handleSidebarClick}
            handleSidebarListItemClick={handleSidebarListItemClick}
          />
        </Grid>

        {/* Display Selected Filters using the new component */}
        {/* Pass individual filter data and removal handlers */}
        <SelectedFiltersDisplay
          selectedRegions={selectedRegions}
          regionOptions={regionOptions}
          handleRemoveRegion={handleRemoveRegion}
          selectedLocations={selectedLocations}
          selectedRegulations={selectedRegulations}
          selectedMarketSizes={selectedMarketSizes}
          selectedSortBy={selectedSortBy}
          selectedBudget={selectedBudget}
          countryOptions={countryOptions}
          regulationOptions={regulationOptions}
          marketSizeOptions={marketSizeOptions}
          sortByOptions={formatSortByOptions(sortByOptions, selectedBudget.type)}
          findLabel={findLabel}
          handleRemoveLocation={handleRemoveLocation}
          handleRemoveRegulation={handleRemoveRegulation}
          handleRemoveMarketSize={handleRemoveMarketSize}
          handleRemoveBudget={() =>
            setSelectedBudget({ type: selectedBudget.type, range: { min: 0, max: 0 } })
          }
          handleSortByChange={handleSortByChange}
          isMobileScreen={isMobileScreen}
          isFreeUser={isFreeUser}
          handleOnSampleClick={handleOnSampleClick}
          currency={currency}
        />

        {/* Rest of the page content */}
        {/* Render the Data Grid */}
        <Grid item xs={12}>
          {/* Pass processed data to the grid */}
          {isLoading ? (
            <LoadingCard height={150} type="bars" />
          ) : (
            <MarketDataGrid
              data={processedMarkets}
              metadata={MARKET_FINDER_COLUMN_METADATA}
              isFreeUser={isFreeUser}
              isLoggedIn={isLoggedIn}
              handleOnSampleClick={handleOnSampleClick}
              currency={currency}
              selectedBudget={selectedBudget}
              currentRequestType={currentRequestType}
              openCreditDeductModal={() => setCreditDeductModalOpen(true)}
              selectedList={selectedListLoading ? 'loading' : selectedList}
              selectedListError={selectedListError}
              showSavedMarketLoadingError={showSavedMarketLoadingError}
              setShowSavedMarketLoadingError={setShowSavedMarketLoadingError}
              handleSidebarClick={handleSidebarClick}
              handleSidebarListItemClick={handleSidebarListItemClick}
            />
          )}
        </Grid>
      </Grid>
      {creditDeductModalOpen && (
        <CreditWarningModal
          open={creditDeductModalOpen}
          handleClose={() => setCreditDeductModalOpen(false)}
          handleOnClick={() => fetchMarkets(true)}
        />
      )}
    </>
  );
};

export default MarketExplorerPage;
